import devHours from "../../components/industries/sections/devHours"

/**
 * /industries/events-production data — rebuilt from
 * src/Design/industries/industry-events-production.html, word for word.
 *
 * Content-to-verify: "Confirm the 50% figure against the Centric Events
 * case study. The old page said 'guaranteed'; this design calls it a
 * target. Check features and FAQ answers against the events offer page."
 * A dedicated Centric Events case study now exists at
 * /caseStudies/projects/centricEvents — "Read the case study" below links
 * there. The design's own "50% margin target" phrasing (not "guaranteed")
 * is used as written — that
 * offer entry's own proof.summary still says "guaranteed", but the design
 * file's correction is deliberate (a margin outcome isn't something to
 * guarantee), so "target" is kept. Features checked against `included`
 * (automated intake, real-time costing, change orders billed, dashboards
 * all match). "253k Development hours" uses devHours(), not a snapshot.
 *
 * Title tag and meta description are unchanged from the previous version of
 * this page.
 */
const eventsProduction = ({ heroImage, challengeImages = [], solutionImage, resultsImage }) => ({
  slug: "events-production",
  industryName: "Events & Production",

  kicker: "Solutions for teams running several events at once",
  headline: "Stop rebuilding every event from a blank spreadsheet",
  subheading: "Vendors, crews, logistics and margins for every event you run, in one place.",
  heroImage,
  heroImageAlt: "A decorated event ballroom with a themed stage backdrop, disco balls and round banquet tables",
  ctaHref: "/contact",
  ctaLabel: "Book your free consultation",
  badge: "Centric Events · 100% of change orders billed",
  stats: [
    { value: "15+", label: "Odoo implementations" },
    { value: "35+", label: "Solutions delivered" },
    { value: `${devHours()}`, label: "Development hours" },
    { value: "30 days", label: "Post-launch warranty" },
  ],

  challengesEyebrow: "What slows events & production teams down",
  challengesHeading: "Four things that eat your margin",
  challengesIntro: "Most production teams hit all four at once, usually on their busiest week.",
  challenges: [
    {
      image: challengeImages[0],
      heading: "Every event from scratch",
      detail: "No repeatable system, so each event is rebuilt from a blank sheet.",
    },
    {
      image: challengeImages[1],
      heading: "Vendor details in inboxes",
      detail: "Venue and supplier information isn't shared across the team.",
    },
    {
      image: challengeImages[2],
      heading: "Parallel events by hand",
      detail: "Staffing and logistics across simultaneous events are tracked manually.",
    },
    {
      image: challengeImages[3],
      heading: "Profit known weeks later",
      detail: "Event-level margins appear long after the event is over.",
    },
  ],

  situationsEyebrow: "How we help events & production teams",
  situationsHeading: "Wherever you're starting from",
  situationsIntro:
    "Every production company works its own way. These are the four situations we see most, and how we handle each.",
  situations: [
    {
      tag: "Every event from scratch",
      title: "One system for every event",
      detail: "Odoo set up for events, with real-time job costing and margin alerts, live in eight weeks.",
      ghostIconPath:
        '<path d="M5 15c-1 1-1.5 3.5-1.5 5.5 2 0 4.5-.5 5.5-1.5"/><path d="M9 15l-3-3c1.5-4 5-8 12-8 0 7-4 10.5-8 12z"/><circle cx="14.5" cy="9.5" r="1.5"/>',
      footerText: "Odoo for events and production",
      footerHref: "/offers/odoo-for-events-and-production",
    },
    {
      tag: "Proposals by hand",
      title: "Quotes and proposals out faster",
      detail: "We automate quotes and documents from your real costs, so they match the job.",
      ghostIconPath: '<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6"/>',
      footerText: "Document automation",
      footerHref: "/offers/ai-quote-and-document-automation",
    },
    {
      tag: "Your process is unusual",
      title: "Built around how you produce",
      detail: "When no product fits, we build the system around your workflow.",
      ghostIconPath:
        '<path d="M12 3a2 2 0 0 1 2 2v1h2a2 2 0 0 1 2 2v2h1a2 2 0 0 1 0 4h-1v2a2 2 0 0 1-2 2h-2v1a2 2 0 0 1-4 0v-1H8a2 2 0 0 1-2-2v-2H5a2 2 0 0 1 0-4h1V8a2 2 0 0 1 2-2h2V5a2 2 0 0 1 2-2z"/>',
      footerText: "Custom development",
      footerHref: "/services/webdevelopment",
    },
    {
      tag: "Tools that don't connect",
      title: "One set of numbers",
      detail: "We connect your booking, accounting and crew tools so information is entered once.",
      ghostIconPath:
        '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>',
      footerText: "Talk to us",
      footerHref: "/contact",
    },
  ],
  situationsUnsureHeading: "Not sure which fits?",
  situationsUnsureDetail: "The Fit Sprint gives you a costed plan before you commit to anything.",
  situationsUnsureLinkLabel: "See the Fit Sprint",
  situationsUnsureLinkHref: "/offers/ops-and-erp-fit-sprint",

  resultsEyebrow: "Results for events and production clients",
  resultsHeading: "Margins protected while the job is still running",
  resultsMetrics: [
    { value: "100%", label: "of change orders tracked and billed, instead of leaking after the job" },
    { value: "50%", label: 'margin target, with "margin killer" alerts when a job drifts below it' },
  ],
  resultsSummary: "Alerts flag a job the moment its margin slips, so the team fixes it before the event, not after the invoice.",
  resultsLinkLabel: "Read the case study",
  resultsLinkHref: "/caseStudies/projects/centricEvents",
  resultsImage,
  resultsImageAlt: "Centric Events",

  // Name resolved against src/data/clientLogos.js by TrustedBy.js.
  trustedByLogos: ["Centric Events"],

  whyEyebrow: "Why Visiomate for events & production",
  whyHeading: "What you get with this team",
  whyIntro: "Three things that stay the same on every engagement.",
  whyLinkHref: "/contact",
  reasons: [
    {
      title: "Same team, start to finish",
      detail: "The people who start your project finish it, with the founder involved throughout.",
    },
    { title: "30-day warranty", detail: "Everything we configure or build is covered for 30 days after go-live." },
    {
      title: "We pick what fits your setup",
      detail: "Odoo, Dynamics 365 or a custom build — we recommend what suits your business, not one product.",
    },
  ],

  questionsEyebrow: "Questions",
  questionsHeading: "Questions buyers ask before they commit",
  questionsIntro: "Something else on your mind?",
  questionsLinkHref: "/contact",
  faqs: [
    {
      q: "Can this handle several events at once?",
      a: "Yes. Each event runs as its own job, and dashboards show every running event together.",
    },
    {
      q: "What if no product fits how we work?",
      a: "We build around your workflow: Odoo set up for your event types, or a custom system where nothing off the shelf fits.",
    },
    {
      q: "Can you connect the tools we already use?",
      a: "Yes. We connect booking, accounting and crew tools so information is entered once.",
    },
    {
      q: "How do we start if we're not sure what we need?",
      a: "With the Fit Sprint: we map how you work and give you a costed plan before you commit to anything.",
    },
  ],

  ctaHeading: "Start with a costed roadmap, not a proposal",
  ctaDetail: "The Ops & ERP Fit Sprint gives you a costed plan before you commit to anything larger.",
  ctaLinkLabel: "See the Fit Sprint",
  ctaLinkHref: "/offers/ops-and-erp-fit-sprint",

  metaTitle: "Events & Production Software Development Company | Visiomate",
  metaDescription:
    "Real-time job costing and margin alerts for AV production and event companies, built in Odoo — live in eight weeks.",
})

export default eventsProduction
